import { Stack, Card, Text, Group, Badge, Box } from '@mantine/core'
import type { VisitWithRelations } from '@markku/types'
import { IconClock, IconMapPin, IconUsers } from '@tabler/icons-react'
import { formatDate } from '@markku/utils'
import { Trans, useLingui } from '@lingui/react/macro'

interface FutureVisitProps {
  visit: VisitWithRelations
}

export function FutureVisit({ visit }: FutureVisitProps) {
  const { t } = useLingui()
  const visitDate = new Date(visit.startTime)
  const endTime = new Date(visit.endTime)

  // Format date and time
  const dayName = visitDate.toLocaleDateString('fi-FI', { weekday: 'long' })
  const dateStr = formatDate(visitDate)
  const startTimeStr = visitDate.toLocaleTimeString('fi-FI', {
    hour: '2-digit',
    minute: '2-digit',
  })
  const endTimeStr = endTime.toLocaleTimeString('fi-FI', {
    hour: '2-digit',
    minute: '2-digit',
  })

  const attendeeCount = visit.visitAttendees?.length || 0
  const taskCount = visit.visitTasks?.length || 0

  return (
    <Stack gap="lg">
      {/* Visit Information Card */}
      <Card withBorder>
        <Text size="lg" fw={600} mb="md">
          <Trans>Visit details</Trans>
        </Text>

        <Stack gap="md">
          <Box>
            <Text size="sm" c="dimmed" mb="xs">
              <Trans>Date and time</Trans>
            </Text>
            <Text size="md" fw={500}>
              {dayName} {dateStr}
            </Text>
            <Group gap="xs" mt="xs">
              <IconClock size={16} color="var(--mantine-color-gray-6)" />
              <Text size="sm">
                {startTimeStr} - {endTimeStr}
              </Text>
            </Group>
          </Box>

          {visit.location && (
            <Box>
              <Text size="sm" c="dimmed" mb="xs">
                <Trans>Location</Trans>
              </Text>
              <Group gap="xs">
                <IconMapPin size={16} color="var(--mantine-color-gray-6)" />
                <Text size="sm">{visit.location}</Text>
              </Group>
            </Box>
          )}

          <Group gap="sm">
            <Badge size="md" variant="light" color="blue">
              <Trans>Scheduled</Trans>
            </Badge>
            {attendeeCount > 0 && (
              <Badge
                size="md"
                variant="light"
                leftSection={<IconUsers size={14} />}
              >
                <Trans>{attendeeCount} attendees</Trans>
              </Badge>
            )}
            {taskCount > 0 && (
              <Badge size="md" variant="light" color="grape">
                <Trans>{taskCount} tasks</Trans>
              </Badge>
            )}
          </Group>

          {visit.notes && (
            <Box>
              <Text size="sm" c="dimmed" mb="xs">
                <Trans>Additional information</Trans>
              </Text>
              <Text size="sm">{visit.notes}</Text>
            </Box>
          )}
        </Stack>
      </Card>

      {/* Planned Tasks */}
      {visit.visitTasks && visit.visitTasks.length > 0 && (
        <Card withBorder>
          <Text size="lg" fw={600} mb="md">
            <Trans>Planned tasks</Trans>
          </Text>

          <Stack gap="sm">
            {visit.visitTasks.map((task, index) => {
              const taskName =
                task.taskName ||
                task.maintenanceTask?.name ||
                t`Task ${index + 1}`
              const taskDescription =
                task.taskDescription || task.maintenanceTask?.description
              const estimatedDuration =
                task.maintenanceTask?.estimatedDurationMinutes

              return (
                <Box
                  key={task.id}
                  pb="sm"
                  style={{
                    borderBottom:
                      index < visit.visitTasks!.length - 1
                        ? '1px solid var(--mantine-color-gray-2)'
                        : undefined,
                  }}
                >
                  <Group justify="space-between" align="flex-start">
                    <Box>
                      <Text size="sm" fw={500}>
                        {taskName}
                      </Text>
                      {taskDescription && (
                        <Text size="xs" c="dimmed" mt="xs">
                          {taskDescription}
                        </Text>
                      )}
                    </Box>
                    {estimatedDuration && (
                      <Badge
                        size="sm"
                        variant="light"
                        color="gray"
                        leftSection={<IconClock size={12} />}
                      >
                        <Trans>~{estimatedDuration} min</Trans>
                      </Badge>
                    )}
                  </Group>
                </Box>
              )
            })}
          </Stack>
        </Card>
      )}

      {/* Attendees */}
      {visit.visitAttendees && visit.visitAttendees.length > 0 && (
        <Card withBorder>
          <Text size="lg" fw={600} mb="md">
            <Trans>Attendees</Trans>
          </Text>

          <Stack gap="xs">
            {visit.visitAttendees.map((attendee) => {
              const user = attendee.user
              if (!user) return null

              return (
                <Group key={attendee.id} gap="sm">
                  <Text size="sm">
                    {user.firstName} {user.lastName[0]}.
                  </Text>
                  {attendee.role && (
                    <Badge size="sm" variant="light">
                      {attendee.role}
                    </Badge>
                  )}
                </Group>
              )
            })}
          </Stack>
        </Card>
      )}
    </Stack>
  )
}
